import { FC, useState, useEffect, ReactNode, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import Form from 'react-bootstrap/Form';
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from "react-hook-form";
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import toast from 'react-hot-toast';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';

// import { CreditCardCsvValidate } from '../../../resources/form-validator';
import { CC_AMOUNT_TYPE, CC_DATE_FORMAT, GL_VP, SELECT_CUSTOM_STYLE } from '../../../resources/constants';
import closeBtn from '../../../assets/images/close-btn.svg';
import { CreditCardCsvI } from '../../../resources/form-props';
import SelectCustomOption from '../../../components/SelectCustomOption';
import { useChartAccountApi } from '../../../resources/hooks/api/chartAccountApiHook';
import { useCreditCardApi } from '../../../resources/hooks/api/creditCardApiHook';
import { getBranch } from '../../../store/user/selectors'

interface Props {
    closeModal: () => void
}

const CreditCardCSVUploadModal: FC<Props> = ({ closeModal }) => {
    const { t } = useTranslation('components.Modal.CreditCardCSVUpload.creditCardCSVUploadModal');
    const { glAccountsList } = useChartAccountApi();
    const { csvUpload, CCUpload, checkYearLock, getLockedYearsList } = useCreditCardApi();

    const branchData = useSelector(getBranch);
    const fileRef = useRef<HTMLInputElement>(null);
    const yearCheckRequestRef = useRef(0);

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i).map(y => ({ label: String(y), value: y }));
    const [selectedYear, setSelectedYear] = useState<{ label: string; value: number }>({ label: String(currentYear), value: currentYear });
    const [yearLocked, setYearLocked] = useState<boolean>(false);
    const [yearLockChecking, setYearLockChecking] = useState<boolean>(false);
    const [yearLockReason, setYearLockReason] = useState<string>('');

    const CreditCardCsvValidate = yup.object().shape({
        step: yup.number(),
        file: yup.mixed()
            .required(t('validation.fileRequired'))
            .test(
                "is-file-of-correct-type",
                t('validation.fileType'),
                () => {
                    let valid = true;
                    const file = fileRef && fileRef?.current && fileRef?.current?.files && fileRef?.current?.files[0] || null;
                    if (file) {
                        const type = file.type.split("/")[1];
                        const validTypes = ["csv"];
                        if (!validTypes.includes(type)) {
                            valid = false;
                        }
                    }
                    return valid;
                }
            ),
        skip_rows: yup.number().typeError(t('validation.invalidSkipRows')).min(0, t('validation.skipRows')),
        master_chart_of_account_id: yup.object().when(['step'], {
            is: (step: number) => {
                return step === 1;
            },
            then: (schema: any) => schema.shape({
                label: yup.string().required(t('validation.accountRequired')),
                value: yup.string().required(t('validation.accountRequired')),
            }).required(t('validation.accountRequired')).typeError(t('validation.accountRequired')),
            otherwise: (schema: any) => schema.nullable()
        }),
        amount_type: yup.object().when(['step'], {
            is: (step: number) => {
                return step === 1;
            },
            then: (schema: any) => schema.shape({
                label: yup.string().required(t('validation.amountTypeRequired')),
                value: yup.string().required(t('validation.amountTypeRequired')),
            }).required(t('validation.amountTypeRequired')).typeError(t('validation.amountTypeRequired')),
            otherwise: (schema: any) => schema.nullable()
        }),
        amount_col: yup.object().when(['step'], {
            is: (step: number) => {
                return step === 1;
            },
            then: (schema: any) => schema.shape({
                label: yup.string().required(t('validation.amountColumnRequired')),
                value: yup.string().required(t('validation.amountColumnRequired')),
            }).required(t('validation.amountColumnRequired')).typeError(t('validation.amountColumnRequired')),
            otherwise: (schema: any) => schema.nullable()
        }),
        credit_col: yup.object().when(['step', 'amount_type'], {
            is: (step: number, amount_type: any) => {
                return step === 1 && (amount_type && +amount_type.value === 3);
            },
            then: (schema: any) => schema.shape({
                label: yup.string().required(t('validation.crdrColumnRequired')),
                value: yup.string().required(t('validation.crdrColumnRequired')),
            }).required(t('validation.crdrColumnRequired')).typeError(t('validation.crdrColumnRequired')),
            otherwise: (schema: any) => schema.nullable()
        }),
        date_format: yup.object().when(['step'], {
            is: (step: number) => {
                return step === 1;
            },
            then: (schema: any) => schema.shape({
                label: yup.string().required(t('validation.dateFormatRequired')),
                value: yup.string().required(t('validation.dateFormatRequired')),
            }).required(t('validation.dateFormatRequired')).typeError(t('validation.dateFormatRequired')),
            otherwise: (schema: any) => schema.nullable()
        }),
        date_col: yup.object().when(['step'], {
            is: (step: number) => {
                return step === 1;
            },
            then: (schema: any) => schema.shape({
                label: yup.string().required(t('validation.dateColumnRequired')),
                value: yup.string().required(t('validation.dateColumnRequired')),
            }).required(t('validation.dateColumnRequired')).typeError(t('validation.dateColumnRequired')),
            otherwise: (schema: any) => schema.nullable()
        }),
        description_col: yup.object().when(['step'], {
            is: (step: number) => {
                return step === 1;
            },
            then: (schema: any) => schema.shape({
                label: yup.string().required(t('validation.descriptionColumnRequired')),
                value: yup.string().required(t('validation.descriptionColumnRequired')),
            }).required(t('validation.descriptionColumnRequired')).typeError(t('validation.descriptionColumnRequired')),
            otherwise: (schema: any) => schema.nullable()
        })
    });

    const { control, register, watch, handleSubmit, getValues, resetField, reset, setValue, formState: { errors } } = useForm<CreditCardCsvI>({
        defaultValues: {
            step: 0,
            master_chart_of_account_id: null,
            file: null,
            skip_rows: 0,
            amount_type: null,
            amount_col: null,
            credit_col: null,
            date_format: null,
            date_col: null,
            description_col: null
        },
        resolver: yupResolver(CreditCardCsvValidate)
    });

    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [selectCustomStyle, setSlectCustomStyle] = useState<any>(SELECT_CUSTOM_STYLE('#fff'));
    const [csvColumns, setCsvColumns] = useState<any[]>([]);
    const [formInput, setFormInput] = useState<any>({
        file: null,
        skip_rows: 0,
    });

    const watchAmountType = watch('amount_type', 1);

    const fetchYearBlockStatus = (year: number): Promise<{ locked: boolean; reason: string }> => {
        return new Promise((resolve) => {
            checkYearLock(
                { branch_id: +branchData['id'], year, section: 2 },
                (_msg: string, resp: any) => {
                    resolve({
                        locked: resp?.data?.data?.locked === true,
                        reason: resp?.data?.data?.reason || '',
                    });
                },
                () => {
                    resolve({ locked: false, reason: '' });
                }
            );
        });
    };

    const fetchExplicitLockedYears = (): Promise<number[]> => {
        return new Promise((resolve) => {
            getLockedYearsList(
                { branch_id: +branchData['id'], section: 2 },
                (_msg: string, resp: any) => {
                    const years = Array.isArray(resp?.data?.data?.locked_years)
                        ? resp.data.data.locked_years
                            .map((y: any) => Number(y))
                            .filter((y: number) => Number.isFinite(y))
                        : [];
                    resolve(years);
                },
                () => resolve([])
            );
        });
    };

    // When the selected year changes, check lock status against the GL API.
    const handleYearChange = async (option: { label: string; value: number }) => {
        const requestId = ++yearCheckRequestRef.current;
        setSelectedYear(option);
        setYearLocked(false);
        setYearLockReason('');
        setYearLockChecking(true);

        const [selectedYearStatus, explicitLockedYears] = await Promise.all([
            fetchYearBlockStatus(option.value),
            fetchExplicitLockedYears(),
        ]);

        if (requestId !== yearCheckRequestRef.current) {
            return;
        }

        const lockedYears = [...explicitLockedYears].sort((a, b) => a - b);

        const earliestLockedFinancialYear = lockedYears.length > 0 ? lockedYears[0] : null;
        const isEarlierThanLockedFinancialYear =
            earliestLockedFinancialYear !== null && option.value < earliestLockedFinancialYear;

        const blocked = selectedYearStatus.locked || isEarlierThanLockedFinancialYear;
        const reason = isEarlierThanLockedFinancialYear
            ? 'earlier_than_locked_financial_year'
            : (selectedYearStatus.reason || '');

        setYearLocked(blocked);
        setYearLockReason(reason);
        setYearLockChecking(false);
    };

    // Initial lock status and locked-years fetch.
    useEffect(() => {
        handleYearChange({ label: String(currentYear), value: currentYear });
    }, []);

    const getLocalizedValidationMessage = (message?: string, resp?: any) => {
        const fallbackMessage = message || t('validation.genericError');
        const errorType = resp?.error?.type || resp?.type || '';

        if (errorType === 'year_mismatch') {
            const selectedYearFromResp = resp?.error?.selected_year;
            const csvYearsFromResp = resp?.error?.csv_years;
            const csvYears = Array.isArray(csvYearsFromResp)
                ? csvYearsFromResp.join(', ')
                : (csvYearsFromResp || '');
            return t('validation.yearMismatch', {
                selectedYear: selectedYearFromResp || selectedYear.value,
                csvYears,
            });
        }

        if (errorType === 'year_locked' || errorType === 'earlier_than_locked_financial_year') {
            return t('validation.yearLockedOrEarlier');
        }

        if (errorType === 'tp_data_till' || errorType === 'tp_data_locked_till') {
            const tpTillDate = resp?.error?.till_date || resp?.error?.date || resp?.till_date || resp?.date;
            return tpTillDate ? t('validation.tpDataTill', { date: tpTillDate }) : fallbackMessage;
        }

        // Fallback for legacy/backend plain-English responses without a stable error type.
        const tpDataPrefixPatterns = [
            /^you cannot add transactions as you already have tp data till\s*/i,
            /^you cannot add transactions as you already have third party data till\s*/i,
        ];

        for (const pattern of tpDataPrefixPatterns) {
            if (pattern.test(fallbackMessage.trim())) {
                const tpTillDate = fallbackMessage.replace(pattern, '').trim();
                return tpTillDate ? t('validation.tpDataTill', { date: tpTillDate }) : t('validation.tpDataTillNoDate');
            }
        }

        return fallbackMessage;
    };

    const getLocalizedSuccessMessage = (operation: 'csvUpload' | 'ccUpload', fallbackMessage?: string) => {
        const operationMessageMap = {
            csvUpload: t('toast.fileUploadedSuccessfully'),
            ccUpload: t('toast.changesSavedSuccessfully'),
        } as const;

        return operationMessageMap[operation] || fallbackMessage || t('toast.genericSuccess');
    };

    const getYearLockMessage = () => {
        return t('validation.yearLockedOrEarlier');
    };
    const fechAccountList = (searchText: string = ''): Promise<any[]> => {
        return new Promise<any>((resolve, reject) => {
            const params: any = {
                "page": 1,
                "per_page": 10,
                branch_id: +branchData['id']
            }
            if (searchText) {
                params['name'] = searchText;
            }
            /* GLD-230 */
            // params['parent_account_in'] = GL_VP.CREDIT_CARD.account.join(',');
            // params['include_assigned_vp'] = 1;
            params['parent_account_in'] = GL_VP.CREDIT_CARD.account.join(',');

            glAccountsList(params, (message: string, resp: any) => {
                const data = resp.data.data.result.map((e: any) => ({
                    label: e['name'] + `(${e['account']})`,
                    value: e['id'],
                    account: e['account']
                }));
                return resolve(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve([]);
            })
        })
    }

    const filterAccountList = async (inputValue: string) => {
        const data = await fechAccountList(inputValue)
        return data;
    };

    const processData = (values: any) => {
        const formData = new FormData();
        formData.append('file', values.file);

        if (values.step === 0) {
            setShowLoader(true);

            const params = {
                skip_rows: values.skip_rows
            }

            csvUpload({ formData, params }, (message: string, resp: any) => {
                setShowLoader(false);
                const columns = resp.data.data.map((e: string) => ({ label: e, value: e }));
                setCsvColumns(columns);
                setValue('step', 1);
                toast.success(getLocalizedSuccessMessage('csvUpload', message));
            }, (message: string, resp: any) => {
                setShowLoader(false);
                toast.error(getLocalizedValidationMessage(message, resp));
            });
        } else {
            formData.append('master_chart_of_account_id', values.master_chart_of_account_id.value);
            formData.append('amount_type', values.amount_type.value);
            formData.append('amount_col', values.amount_col.value);
            formData.append('date_format', values.date_format.value);
            formData.append('date_col', values.date_col.value);
            formData.append('description_col', values.description_col.value);
            formData.append('skip_rows', values.skip_rows);
            formData.append('year', String(selectedYear.value));

            const params: any = {
                branch_id: +branchData['id']
            }
            if (watchAmountType?.value === 3) {
                formData.append('credit_col', values.credit_col.value);
            }

            if (yearLocked) {
                toast.error(getYearLockMessage());
                return;
            }

            setShowLoader(true);

            CCUpload({ formData, params }, (message: string, resp: any) => {
                setShowLoader(false);
                toast.success(getLocalizedSuccessMessage('ccUpload', message));
                _handleCloseModal();
            }, (message: string, resp: any) => {
                setShowLoader(false);
                toast.error(getLocalizedValidationMessage(message, resp));
            });
        }
    }

    const _handleCloseModal = () => {
        reset();
        closeModal();
    }

    return (
        <Modal show={true} onHide={_handleCloseModal} backdrop="static" animation={false} size="lg">
            <div className="">
                <div className="">
                    <div className="modal-header">
                        <h5 className="modal-title" id="exampleModalLabel">{t('title')}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={_handleCloseModal}><img src={closeBtn} alt="" /></button>
                    </div>
                    <form onSubmit={handleSubmit(processData)}>
                        <div className="modal-body pb-0">
                            {/* Year selector — always visible so the user declares which year they're uploading for */}
                            <div className='row align-items-center mb-2'>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label>{t('label.year') || 'Upload Year'}<strong className='text-danger'>*</strong></label>
                                        <Select
                                            options={yearOptions}
                                            value={selectedYear}
                                            onChange={(opt: any) => handleYearChange(opt)}
                                            isDisabled={getValues('step') === 1}
                                            styles={selectCustomStyle}
                                        />
                                        {yearLockChecking && <small className="text-muted">{t('validation.checkingLockStatus')}</small>}
                                        {yearLocked && !yearLockChecking && (
                                            <small className="text-danger">
                                                {getYearLockMessage()}
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className='row align-items-center'>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="">{t('label.selectFile')}<strong className='text-danger'>*</strong></label>
                                        <Controller
                                            control={control}
                                            name="file"
                                            render={({ field: { onChange, name, disabled } }) => (
                                                <div style={{ position: 'relative',margin:"0",padding:"0", cursor: "pointer" }} className="form-control">
                                                    <input
                                                        type="file"
                                                        ref={fileRef}
                                                        accept=".csv"
                                                        name={name}
                                                        onChange={(e: any) => {
                                                            onChange(e.target.files[0]);
                                                            setFormInput({
                                                                ...formInput,
                                                                file: e.target.files[0]
                                                            });
                                                        }}
                                                        className="form-control"
                                                        style={{ display: 'none' }}
                                                        disabled={getValues('step') === 1}
                                                        id="customFileInput"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => fileRef.current && fileRef.current.click()}
                                                        disabled={getValues('step') === 1}
                                                        style={{height: "100%",borderRadius: "8px 0 0 8px"}}
                                                    >
                                                        {t('button.chooseFile')}
                                                    </button>
                                                    <span
                                                        style={{ marginLeft: 10, maxWidth: 200, display: 'inline-block', verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',}}
                                                        onClick={() => fileRef.current && fileRef.current.click()}
                                                    >
                                                        {formInput.file
                                                            ? (formInput.file.name.length > 30
                                                                ? formInput.file.name.slice(0, 15) + '...' + formInput.file.name.slice(-12)
                                                                : formInput.file.name)
                                                            : t('placeholder.noFileChosen')}
                                                    </span>
                                                </div>
                                            )}
                                        />
                                        {
                                            errors && errors.file && errors.file.message && (
                                                <Form.Text className="text-danger">
                                                    {errors.file.message as ReactNode || ''}
                                                </Form.Text>
                                            ) || ''
                                        }
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="">{t('label.skipRows')}</label>
                                        <Controller
                                            control={control}
                                            name="skip_rows"
                                            render={({ field: { name, onChange} }) => (
                                                <input
                                                    name={name}
                                                    onChange={(e: any) => {
                                                        onChange(e);
                                                        setFormInput({
                                                            ...formInput,
                                                            skip_rows: e.target.value
                                                        });
                                                    }}
                                                    type="number"
                                                    placeholder={t('placeholder.skipRows')}
                                                    className="form-control"
                                                    disabled={getValues('step') === 1}
                                                />
                                            )}
                                        />
                                        {
                                            errors && errors.skip_rows && errors.skip_rows.message && (
                                                <Form.Text className="text-danger">
                                                    {errors.skip_rows.message as ReactNode || ''}
                                                </Form.Text>
                                            ) || ''
                                        }
                                    </div>
                                </div>
                                <div className="col-md-1">
                                    {
                                        getValues('step') === 1 && (
                                            <button type="button" className="btn btn-warning" onClick={() => reset({ "step": 0, file: formInput['file'], skip_rows: formInput['skip_rows'] })}>{t('button.edit')}</button>
                                        )
                                    }

                                </div>
                                <span><strong>{t('note')}</strong> <p>{t('note.skipRows')}</p></span>
                            </div>

                            {
                                getValues('step') === 1 && (
                                    <div className="">
                                        <div className="row">
                                            <div className="col-sm-6">
                                                <div className="form-group">
                                                    <label htmlFor="">{t('label.selectCreditCard')}<strong className='text-danger'>*</strong></label>
                                                    <Controller
                                                        control={control}
                                                        name={`master_chart_of_account_id`}
                                                        render={({ field }) => (
                                                            <AsyncSelect
                                                                {...field}
                                                                isClearable={true}
                                                                placeholder={t('placeholder.selectAny')}
                                                                defaultOptions
                                                                loadOptions={filterAccountList}
                                                                className=''
                                                                menuPosition='fixed'
                                                                styles={selectCustomStyle}
                                                                components={{ Option: SelectCustomOption }}
                                                            />
                                                        )}
                                                    />
                                                    {
                                                        errors && errors.master_chart_of_account_id && errors.master_chart_of_account_id?.message && (
                                                            <Form.Text className="d-flex text-danger">
                                                                {errors.master_chart_of_account_id.message as ReactNode || ''}
                                                            </Form.Text>
                                                        ) || ''
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row m-2">
                                            <div className="col-sm-12 d-flex justify-content-around">
                                                <h5 className="text-center">{t('label.columnMapping')}</h5>
                                                <h5 className="text-center">{t('label.csvHeader')}</h5>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-sm-6">
                                                <div className='form-group'>
                                                    <label htmlFor="">{t('label.defineCreditCardCharge')}<strong className='text-danger'>*</strong></label>
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="form-group">
                                                    <Controller
                                                        control={control}
                                                        name={`amount_type`}
                                                        render={({ field }) => (
                                                            <Select
                                                                {...field}
                                                                options={CC_AMOUNT_TYPE}
                                                                isClearable={true}
                                                                placeholder={t('placeholder.selectAmountType')}
                                                                className=''
                                                                menuPosition='fixed'
                                                                styles={selectCustomStyle}
                                                                components={{ Option: SelectCustomOption }}
                                                            />
                                                        )}
                                                    />
                                                    {
                                                        errors && errors.amount_type && errors.amount_type.message && (
                                                            <Form.Text className="text-danger">
                                                                {errors.amount_type.message as ReactNode || ''}
                                                            </Form.Text>
                                                        ) || ''
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-sm-6">
                                                <div className='form-group'>
                                                    <label htmlFor="">{t('label.creditAmountColumn')}<strong className='text-danger'>*</strong></label>
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="form-group">
                                                    <Controller
                                                        control={control}
                                                        name={`amount_col`}
                                                        render={({ field }) => (
                                                            <Select
                                                                {...field}
                                                                options={csvColumns}
                                                                isClearable={true}
                                                                placeholder={t('placeholder.selectAmountColumn')}
                                                                className=''
                                                                menuPosition='fixed'
                                                                styles={selectCustomStyle}
                                                                components={{ Option: SelectCustomOption }}
                                                            />
                                                        )}
                                                    />

                                                    {/* <input type="text" className="form-control" {...register('amount_col')} placeholder='Amount column' /> */}
                                                    {
                                                        errors && errors.amount_col && errors.amount_col.message && (
                                                            <Form.Text className="text-danger">
                                                                {errors.amount_col.message as ReactNode || ''}
                                                            </Form.Text>
                                                        ) || ''
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        {
                                            watchAmountType?.value === 3 && (
                                                <div className="row">
                                                    <div className="col-sm-6">
                                                        <div className='form-group'>
                                                            <label htmlFor="">{t('label.crdrDefinitionColumn')}<strong className='text-danger'>*</strong></label>
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-6">
                                                        <div className="form-group">
                                                            <Controller
                                                                control={control}
                                                                name={`credit_col`}
                                                                render={({ field }) => (
                                                                    <Select
                                                                        {...field}
                                                                        options={csvColumns}
                                                                        isClearable={true}
                                                                        placeholder={t('placeholder.selectCreditColumn')}
                                                                        className=''
                                                                        menuPosition='fixed'
                                                                        styles={selectCustomStyle}
                                                                        components={{ Option: SelectCustomOption }}
                                                                    />
                                                                )}
                                                            />
                                                            {
                                                                errors && errors.credit_col && errors.credit_col.message && (
                                                                    <Form.Text className="text-danger">
                                                                        {errors.credit_col.message as ReactNode || ''}
                                                                    </Form.Text>
                                                                ) || ''
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            ) || ''
                                        }

                                        <div className="row">
                                            <div className="col-sm-6">
                                                <div className='form-group'>
                                                    <label htmlFor="">{t('label.defineDateFormat')}<strong className='text-danger'>*</strong></label>
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="form-group">
                                                    <Controller
                                                        control={control}
                                                        name={`date_format`}
                                                        render={({ field }) => (
                                                            <Select
                                                                {...field}
                                                                options={CC_DATE_FORMAT}
                                                                isClearable={true}
                                                                placeholder={t('placeholder.selectDateFormat')}
                                                                className=''
                                                                menuPosition='fixed'
                                                                styles={selectCustomStyle}
                                                                components={{ Option: SelectCustomOption }}
                                                            />
                                                        )}
                                                    />
                                                    {
                                                        errors && errors.date_format && errors.date_format.message && (
                                                            <Form.Text className="text-danger">
                                                                {errors.date_format.message as ReactNode || ''}
                                                            </Form.Text>
                                                        ) || ''
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-sm-6">
                                                <div className='form-group'>
                                                    <label htmlFor="">{t('label.dateColumn')}<strong className='text-danger'>*</strong></label>
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="form-group">
                                                    <Controller
                                                        control={control}
                                                        name={`date_col`}
                                                        render={({ field }) => (
                                                            <Select
                                                                {...field}
                                                                options={csvColumns}
                                                                isClearable={true}
                                                                placeholder={t('placeholder.selectDateColumn')}
                                                                className=''
                                                                menuPosition='fixed'
                                                                styles={selectCustomStyle}
                                                                components={{ Option: SelectCustomOption }}
                                                            />
                                                        )}
                                                    />
                                                    {/* <input type="text" className="form-control" {...register('date_col')} placeholder='Date column' /> */}
                                                    {
                                                        errors && errors.date_col && errors.date_col.message && (
                                                            <Form.Text className="text-danger">
                                                                {errors.date_col.message as ReactNode || ''}
                                                            </Form.Text>
                                                        ) || ''
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-sm-6">
                                                <div className='form-group'>
                                                    <label htmlFor="">{t('label.descriptionColumn')}<strong className='text-danger'>*</strong></label>
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="form-group">
                                                    {/* <input type="text" className="form-control" {...register('description_col')} placeholder='Description Column' /> */}
                                                    <Controller
                                                        control={control}
                                                        name={`description_col`}
                                                        render={({ field }) => (
                                                            <Select
                                                                {...field}
                                                                options={csvColumns}
                                                                isClearable={true}
                                                                placeholder={t('placeholder.selectDescriptionColumn')}
                                                                className=''
                                                                menuPosition='fixed'
                                                                styles={selectCustomStyle}
                                                                components={{ Option: SelectCustomOption }}
                                                            />
                                                        )}
                                                    />
                                                    {
                                                        errors && errors.description_col && errors.description_col.message && (
                                                            <Form.Text className="text-danger">
                                                                {errors.description_col.message as ReactNode || ''}
                                                            </Form.Text>
                                                        ) || ''
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }

                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-cancel" onClick={_handleCloseModal}>{t('button.close')}</button>
                            {
                                getValues('step') === 0 && (
                                    <button type="submit" className="btn btn-primary" disabled={showLoader || yearLocked || yearLockChecking}>
                                        {showLoader && (<Spinner animation="border" size="sm" />) || t('button.uploadFile')}
                                    </button>
                                )
                            }
                            {
                                getValues('step') === 1 && (
                                    <button type="submit" className="btn btn-primary" disabled={showLoader || yearLocked || yearLockChecking}>
                                        {showLoader && (<Spinner animation="border" size="sm" />) || t('button.saveChanges')}
                                    </button>
                                )
                            }
                        </div>
                    </form>
                </div>
            </div>
            {/* </div> */}
        </Modal>
    )
}

export default CreditCardCSVUploadModal;